/**
 * ApprovalDialog Component
 *
 * Dialog for approving lesson answers with points input.
 * Used in Lesson Answers Report page for admin approval workflow.
 *
 * Features:
 * - Displays lesson answer context (student, lesson, participants)
 * - Points input with validation (required, min=0)
 * - Form validation with react-hook-form + Zod
 * - Warning message about irreversible action
 *
 * Created: 2025-11-14 (Points System Implementation)
 */

import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import type { LessonAnswerReportDto } from '@/api/generated/types.gen';

/**
 * Form validation schema
 *
 * SE-32/33: CLASS_EXERCISE se aprobă cu numărul de fișe validate (backendul calculează
 * 8 x fișe), nu cu un punctaj literal — celelalte tipuri păstrează punctajul. Ramura
 * activă se citește dintr-un ref (vezi `isClassExerciseRef` mai jos), ca schema să rămână
 * o singură instanță stabilă în timp ce dialogul e refolosit pentru răspunsuri diferite.
 */
function buildApprovalFormSchema(isClassExerciseRef: { current: boolean }) {
  return z
    .object({
      points: z.number().optional(),
      fise: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      if (isClassExerciseRef.current) {
        if (data.fise === undefined || Number.isNaN(data.fise) || data.fise < 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['fise'],
            message: 'Numărul de fișe validate este obligatoriu',
          });
        }
      } else if (data.points === undefined || Number.isNaN(data.points) || data.points < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['points'],
          message: 'Punctajul este obligatoriu',
        });
      }
    });
}

type ApprovalFormData = { points?: number; fise?: number };

/**
 * Component Props
 */
interface ApprovalDialogProps {
  /**
   * Whether the dialog is open
   */
  isOpen: boolean;

  /**
   * Callback when dialog should close
   */
  onClose: () => void;

  /**
   * Callback when approval is confirmed.
   * SE-32/33: `fise` pentru CLASS_EXERCISE, `points` pentru celelalte tipuri — niciodată amândouă.
   */
  onApprove: (value: { points?: number; fise?: number }) => Promise<void>;

  /**
   * The lesson answer to approve
   */
  lessonAnswer: LessonAnswerReportDto | null;

  /**
   * Whether the approval operation is in progress
   */
  loading?: boolean;
}

/**
 * ApprovalDialog Component
 *
 * Displays a dialog for approving lesson answers with points input.
 *
 * @example
 * ```tsx
 * <ApprovalDialog
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onApprove={async (points) => {
 *     await mutation.mutateAsync({ id: answerId, points });
 *   }}
 *   lessonAnswer={selectedAnswer}
 *   loading={mutation.isPending}
 * />
 * ```
 */
export function ApprovalDialog({
  isOpen,
  onClose,
  onApprove,
  lessonAnswer,
  loading = false,
}: ApprovalDialogProps) {
  const isClassExercise = lessonAnswer?.lesson?.type === 'CLASS_EXERCISE';
  const isClassExerciseRef = useRef(isClassExercise);
  isClassExerciseRef.current = isClassExercise;

  const schema = useMemo(() => buildApprovalFormSchema(isClassExerciseRef), []);

  const form = useForm<ApprovalFormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      points: 0,
      fise: 0,
    },
  });

  const fise = form.watch('fise');

  // Provocarea pentru scoala (2026-10-02): punctajul e al provocarii, deci vine precompletat. Cu 0
  // ramas din greseala, aprobarea ar „ocupa" provocarea scolii fara sa-i dea punctele.
  const punctajProvocare =
    lessonAnswer?.lesson?.type === 'SCHOOL_CHALLENGE' ? Number(lessonAnswer.lesson.punctajMaxim ?? 0) : 0;
  const idRaspuns = lessonAnswer?.id;
  useEffect(() => {
    if (isOpen && punctajProvocare > 0) form.setValue('points', punctajProvocare);
  }, [isOpen, punctajProvocare, idRaspuns, form]);

  /**
   * Handle form submission
   */
  const onSubmit = async (data: ApprovalFormData) => {
    try {
      await onApprove(
        isClassExercise ? { fise: data.fise } : { points: data.points }
      );
      handleClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  /**
   * Handle dialog close
   * Resets form state
   */
  const handleClose = () => {
    form.reset();
    onClose();
  };

  if (!lessonAnswer) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Aprobare răspuns lecție</DialogTitle>
          <DialogDescription>
            Introduceți punctajul alocat pentru această lecție.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Context Information */}
            <div className="rounded-md bg-gray-50 p-3 space-y-1">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Elev:</span>{' '}
                {lessonAnswer.user?.nume || '-'}
              </p>
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Lecție:</span>{' '}
                {lessonAnswer.lesson?.title || '-'}
              </p>
              {lessonAnswer.participants && (
                <p className="text-sm text-gray-700">
                  <span className="font-semibold">Număr participanți:</span>{' '}
                  {lessonAnswer.participants}
                </p>
              )}
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Data:</span>{' '}
                {lessonAnswer.timestamp
                  ? new Date(lessonAnswer.timestamp).toLocaleString('ro-RO')
                  : '-'}
              </p>
            </div>

            {/* SE-32/33: CLASS_EXERCISE se aproba pe fise validate (8 x fise = puncte),
                fara camp de puncte literal — celelalte tipuri pastreaza punctajul direct. */}
            {isClassExercise ? (
              <FormField
                control={form.control}
                name="fise"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="form-label-bold">
                      Fișe validate *
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min="0"
                        disabled={loading}
                        placeholder="Introduceți numărul de fișe..."
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === '' ? undefined : parseInt(value, 10));
                        }}
                      />
                    </FormControl>
                    <p className="text-sm text-gray-700">
                      = {8 * (fise ?? 0)} puncte
                    </p>
                    <FormMessage className="form-message-spacer" />
                  </FormItem>
                )}
              />
            ) : (
              <FormField
                control={form.control}
                name="points"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="form-label-bold">
                      Punctaj alocat *
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        min="0"
                        disabled={loading}
                        placeholder="Introduceți punctajul..."
                        onChange={(e) => {
                          const value = e.target.value;
                          field.onChange(value === '' ? 0 : parseInt(value, 10));
                        }}
                      />
                    </FormControl>
                    <FormMessage className="form-message-spacer" />
                  </FormItem>
                )}
              />
            )}

            {/* Warning Message */}
            <div className="lesson-warning-box">
              <p className="text-sm text-yellow-800">
                <span className="font-semibold">Atenție:</span> Această acțiune
                nu poate fi anulată.
              </p>
            </div>

            {/* Actions */}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={loading}
              >
                Anulare
              </Button>
              <Button
                type="submit"
                variant="default"
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                {loading ? 'Se procesează...' : 'Aprobă'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
